import { useMemo, useState, useCallback } from 'react';
import { GetQuestionnaireOutput } from 'graphql/generated/remote-schema-hasura';
import {
  QuestionHelperTextData,
  QuestionnaireQuestionsQuestion,
} from 'graphql/generated/hasura';
import { DynamicQuestionnaireDependencie } from '../../dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';

/* eslint-disable @typescript-eslint/no-explicit-any */
// ---- schema-aligned types ----
type UIControl =
  | 'RADIO_BUTTON'
  | 'LOOKUP_WITH_MULTISELECT'
  | 'LOOKUP_FIELD'
  | 'TEXT_FIELD'
  | 'TEXT_AREA'
  | 'DOUBLE_TEXT_COMBO_L_HELPER_TEXT'
  | 'DOUBLE_DROPDOWN_COMBO'
  | string;

export type Question = {
  id: number | string;
  text: string | null;
  subtitle?: string | null;
  ui_control: UIControl;
  answers?: string[] | null;
  dont_know_answer?: string | null;
  double_text_combo_helper_texts?: QuestionHelperTextData | null;
  dropdown_combo_answers?: {
    dropdown_1_answers?: string[];
    dropdown_2_answers?: string[];
  } | null;
  dropdown_label_1?: string | null;
  dropdown_label_2?: string | null;
  measurement_unit?: string | null;
  response_property?: string | null;
  has_tooltip?: boolean;
  tooltip_content?: string | null;
  placeholder?: string | null;
  none_option?: string | null;
  all_option?: string | null;
  helper_text?: string | null;
  children: Question[];
  array_field_config?: {
    add_button_label: string;
    max_items: number;
    min_items: number;
  };
};

export type Section = {
  name: string;
  properties: string[]; // ids as strings
  complete?: boolean | null;
  dependencies?: DynamicQuestionnaireDependencie[];
};

// what we persist between steps
export type PersistentEntry = {
  questionID: number;
  response: any; // string | string[] | object
  section?: string; // optional
};

// ---- default value helper ----
function defaultForQuestion(q: Question, persisted: any) {
  switch (q.ui_control) {
    // two-slot inputs -> as array, you will register "id[0]" and "id[1]"
    case 'DOUBLE_TEXT_COMBO_L_HELPER_TEXT':
    case 'DOUBLE_DROPDOWN_COMBO': {
      const arr = Array.isArray(persisted) ? persisted : [];
      return [arr[0] ?? '', arr[1] ?? ''];
    }
    // multi-select
    case 'LOOKUP_WITH_MULTISELECT': {
      return Array.isArray(persisted) ? persisted : [];
    }
    // single-value
    case 'TEXT_FIELD':
    case 'TEXT_AREA':
    case 'LOOKUP_FIELD':
    case 'RADIO_BUTTON':
    default: {
      if (persisted == null) return '';
      return persisted;
    }
  }
}

// ---- required collector from dependencies.then.required ----
// NOTE: for now we treat them as "always required" (ignoring 'if' conditions).
// If you later want conditional rules, you can wire 'when()' with RHF values.
function collectRequiredIds(section?: Section) {
  const set = new Set<string>();
  if (!section?.dependencies) return set;
  for (const d of section.dependencies) {
    const req = (d as any)?.then?.required as string[] | undefined;
    req?.forEach((id) => set.add(id));
  }
  return set;
}

// ---- Yup schema builder (pass *your* yup in) ----
function buildYupSchemaForSection(
  yup: any,
  section: Section | undefined | null,
  questionsById: Record<string, Question>,
) {
  const shape: Record<string, any> = {};

  // If section is not provided, return an empty Yup object schema
  if (!section) return yup.object().shape(shape);

  // Collect all required question IDs for this section
  const requiredIds = collectRequiredIds(section);

  // Loop through all questions in the section
  for (const questionIdStr of section.properties) {
    const question = questionsById[questionIdStr];
    if (!question) continue;

    const key = String(question.id);
    const isRequired = requiredIds.has(questionIdStr);

    switch (question.ui_control) {
      case 'DOUBLE_TEXT_COMBO_L_HELPER_TEXT':
      case 'DOUBLE_DROPDOWN_COMBO': {
        let arraySchema = yup.array().of(yup.string());

        if (isRequired) {
          arraySchema = arraySchema
            .min(2, 'Both fields are required')
            .test(
              'two-non-empty',
              'Both fields are required',
              (valueArray: any[]) => {
                const firstValue = valueArray?.[0];
                const secondValue = valueArray?.[1];
                return (
                  Boolean(firstValue && String(firstValue).trim()) &&
                  Boolean(secondValue && String(secondValue).trim())
                );
              },
            );
        } else {
          arraySchema = arraySchema.max(2);
        }

        shape[key] = arraySchema.default(['', '']);
        break;
      }

      case 'LOOKUP_WITH_MULTISELECT': {
        let arraySchema = yup.array().of(yup.string());
        if (isRequired)
          arraySchema = arraySchema.min(1, 'Select at least one option');
        shape[key] = arraySchema.default([]);
        break;
      }

      case 'RADIO_BUTTON': {
        let stringSchema = yup.string();
        if (isRequired)
          stringSchema = stringSchema.required(
            'Required question must be answered',
          );
        shape[key] = stringSchema.default('');
        break;
      }

      case 'TEXT_FIELD':
      case 'TEXT_AREA':
      case 'LOOKUP_FIELD':
      default: {
        let stringSchema = yup.string();
        if (isRequired)
          stringSchema = stringSchema.required(
            'Required question must be answered',
          );
        shape[key] = stringSchema.default('');
        break;
      }
    }
  }

  return yup.object().shape(shape);
}

export const normalizeQuestion = (
  raw: QuestionnaireQuestionsQuestion,
): Question => {
  const rawChildren = (raw?.children ?? []) as any[];

  const children: Question[] = rawChildren
    .map(
      (c) =>
        (c?.questionnaire_questions_question ?? c?.question ?? c) as Question,
    )
    .filter(Boolean)
    .map((child) => ({
      ...child,
      id: child.id as string | number,
      children: (child as any)?.children
        ? ((child as any).children as any[]).map((g) =>
            normalizeQuestion(
              g?.questionnaire_questions_question ?? g?.question ?? g,
            ),
          )
        : [],
    }));

  return {
    ...raw,
    children,
  } as Question;
};

export function useQuestionnaireSections(
  schema?: GetQuestionnaireOutput | null,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  sectionIndex: number,
) {
  const sections = schema?.data ?? [];

  // id -> Question
  const questionsById = useMemo(() => {
    const map: Record<string, Question> = {};
    (schema?.questionnaires_questionnaire_questions ?? []).forEach((e) => {
      const qRaw = e.questionnaire_questions_question;
      const q = normalizeQuestion(qRaw);
      map[String(q.id)] = q;
    });
    return map;
  }, [schema]);

  const totalSections = sections.length;
  const currentSection = sections[sectionIndex];

  // persisted answers across steps
  const [persistentData, setPersistentData] = useState<PersistentEntry[]>([]);

  // list of questions for current section
  const questionList = useMemo(() => {
    if (!currentSection) return [] as Question[];
    return currentSection.properties
      .map((idStr) => questionsById[idStr])
      .filter(Boolean) as Question[];
  }, [currentSection, questionsById]);

  // defaultValues for RHF (ids as keys; arrays for multi-slot)
  const getDefaultValues = useCallback(() => {
    if (!currentSection) return {};
    const defaults: Record<string, any> = {};
    for (const idStr of currentSection.properties) {
      const q = questionsById[idStr];
      if (!q) continue;
      const persisted = persistentData.find(
        (e) => e.questionID === q.id,
      )?.response;
      defaults[String(q.id)] = defaultForQuestion(q, persisted);
    }
    return defaults;
  }, [currentSection, questionsById, persistentData]);

  // persist only current section values (values are by pure ids)
  const saveCurrentSection = useCallback(
    (formValues: Record<string, any>) => {
      if (!currentSection) return;

      const updatedEntries = currentSection.properties
        .map((idStr) => {
          const id = Number(idStr);
          const q = questionsById[idStr];
          if (!q) return null;
          const value = formValues?.[String(id)];
          return {
            questionID: id,
            response: value,
            section: currentSection.name,
          };
        })
        .filter(Boolean) as PersistentEntry[];

      setPersistentData((prev) => {
        const idsInSection = new Set(updatedEntries.map((e) => e.questionID));
        const others = prev.filter((e) => !idsInSection.has(e.questionID));
        return [...others, ...updatedEntries];
      });
    },
    [currentSection, questionsById],
  );

  // optional helpers for payloads
  const toSectionPayload = useCallback(
    (values: Record<string, any>) => {
      if (!currentSection)
        return [] as Array<{ questionID: number; response: any }>;
      return currentSection.properties.map((idStr) => {
        const id = Number(idStr);
        return { questionID: id, response: values[String(id)] };
      });
    },
    [currentSection],
  );

  const toAllPayloadFromPersisted = useCallback(() => {
    return persistentData
      .sort((a, b) => a.questionID - b.questionID)
      .map(({ questionID, response, section }) => ({
        questionID,
        response,
        section,
      }));
  }, [persistentData]);

  // expose a builder to get Yup schema for the *current* section
  const buildCurrentSectionYup = useCallback(
    (yupLib: any) =>
      buildYupSchemaForSection(
        yupLib,
        currentSection as Section,
        questionsById,
      ),
    [currentSection, questionsById],
  );

  return {
    // meta
    title: schema?.title,
    subtitle: schema?.subtitle,

    // sections
    sections,
    sectionIndex,
    totalSections,
    currentSection,

    // questions of current section
    questionList,
    questionsById,

    // RHF
    getDefaultValues, // defaults for useForm/reset (ids as keys)
    buildCurrentSectionYup, // pass your `yup` to get a section-level schema

    // persistence / payloads
    persistentData,
    setPersistentData,
    saveCurrentSection,
    toSectionPayload,
    toAllPayloadFromPersisted,
  };
}

export function collectDynamicFieldStates(
  dependencies: DynamicQuestionnaireDependencie[],
  values: Record<string, any>, // current form values
) {
  const enabledFields = new Set<string>();
  const disabledFields = new Set<string>();
  const requiredFields = new Set<string>();

  for (const dep of dependencies) {
    const { if: ifCondition, then } = dep;
    const mainQuestionId = ifCondition.mainQuestion;

    // Check if the condition matches the current form value
    const expectedConst = ifCondition.properties?.[mainQuestionId]?.const;
    const currentValue = values?.[mainQuestionId];

    if (currentValue !== expectedConst) {
      continue; // condition not met, skip
    }

    // Add enabled fields
    if (Array.isArray(then?.enabled)) {
      then.enabled.forEach((fieldId) => {
        if (fieldId) enabledFields.add(fieldId);
      });
    }

    // Add disabled fields
    if (Array.isArray(then?.disabled)) {
      then.disabled.forEach((fieldId) => {
        if (fieldId) disabledFields.add(fieldId);
      });
    }

    // Add required fields
    if (Array.isArray(then?.required)) {
      then.required.forEach((fieldId) => {
        if (fieldId) requiredFields.add(fieldId);
      });
    }
  }

  return {
    enabledFields: Array.from(enabledFields),
    disabledFields: Array.from(disabledFields),
    requiredFields: Array.from(requiredFields),
  };
}
